// CORE-8343: Optimise Stops keeps the stop list it loaded when the screen opened. Before Optimise and
// before Create Trips the screen fetches the list again and leaves out any stop that is no longer in it
// (for example, one rescheduled to another date in the meantime). These helpers hold those decisions so
// they can be unit-tested; OptimiseStops.vue does the fetching and the notices.

const MAX_LABELS_IN_NOTICE = 10;

// A standalone stop that came from the unassigned-stops list. Shipments (merged into the same list),
// placeholder objects, breaks and return-to-start entries are never candidates.
export function isListStandaloneStop(item) {
    return !!item && typeof item === 'object' && typeof item.stopId === 'number' && item.stopId > 0 && !item.shipmentId;
}

// The list stops that the fresh list no longer returns, in their original order.
export function findStopsNoLongerListed(listedStops, currentStops) {
    const currentIds = new Set((Array.isArray(currentStops) ? currentStops : []).map((c) => c && c.stopId));

    return (Array.isArray(listedStops) ? listedStops : []).filter(
        (s) => isListStandaloneStop(s) && !currentIds.has(s.stopId)
    );
}

function hasJob(stops) {
    return stops.some((r) => r && (r.stopId > 0 || r.shipmentId > 0));
}

// Removes the given stop ids from each vehicle of an optimised plan. A vehicle left with no real job is
// dropped, because Create Trips makes a trip for every vehicle it is sent. Vehicles that lost nothing are
// returned as the same objects; nothing is mutated.
export function removeStopsFromVehicles(vehicles, stopIds) {
    const result = [];

    (vehicles || []).forEach((vehicle) => {
        if (!vehicle) {
            return;
        }

        const stops = vehicle.stops || [];
        const remaining = stops.filter((r) => !(r && stopIds.has(r.stopId)));
        if (remaining.length === stops.length) {
            result.push(vehicle);
        } else if (hasJob(remaining)) {
            result.push({ ...vehicle, stops: remaining });
        }
    });

    return result;
}

// The list stops that an optimised plan would send. Stops from existing trips are not in the list, so
// they are never picked.
export function listStopsInPlan(listItems, vehicles) {
    const planStopIds = new Set(
        (vehicles || []).flatMap((v) => (v && v.stops) || []).map((r) => r && r.stopId)
    );

    return (Array.isArray(listItems) ? listItems : []).filter(
        (s) => isListStandaloneStop(s) && planStopIds.has(s.stopId)
    );
}

// Same naming rule as the Select Stops list.
function stopLabel(stop) {
    if (stop.sourceReference && stop.sourceReference.trim().length !== 0) {
        return stop.sourceReference;
    }

    return stop.stopRef ? stop.stopRef : `Stop ${stop.stopId}`;
}

// The notice can only say that a stop changed: the fresh list does not tell why it dropped out.
export function describeLeftOutStops(stops) {
    if (!stops || stops.length === 0) {
        return '';
    }

    if (stops.length === 1) {
        return `${stopLabel(stops[0])} was left out because it changed after the stop list was loaded. Its date, status or assignment no longer matches this plan.`;
    }

    const labels = stops.slice(0, MAX_LABELS_IN_NOTICE).map(stopLabel).join(', ');
    const more = stops.length > MAX_LABELS_IN_NOTICE ? ` and ${stops.length - MAX_LABELS_IN_NOTICE} more` : '';

    return `${stops.length} stops were left out because they changed after the stop list was loaded. Their date, status or assignment no longer matches this plan: ${labels}${more}`;
}
